// Universal logger class that only logs in staging environment
class Logger {
  constructor() {
    this.isStaging = process.env.REACT_APP_ENVIRONMENT !== 'production';
  }

  // Helper method to get the calling class name
  getCallerName() {
    const stack = new Error().stack;
    const stackLines = stack.split('\n');
    
    // Skip the current function, the logger method, and find the actual caller
    // Stack lines: [0] Error, [1] getCallerName, [2] logger method, [3+] actual callers
    for (let i = 3; i < stackLines.length; i++) {
      const line = stackLines[i];
      // Look for class patterns like "ClassName." or "function ClassName"
      const classMatch = line.match(/at\s+([A-Z][a-zA-Z0-9]*)\./) || 
                        line.match(/at\s+([A-Z][a-zA-Z0-9]*)\s*\(/) ||
                        line.match(/([A-Z][a-zA-Z0-9]*)\./);
      
      if (classMatch && classMatch[1] && classMatch[1] !== 'Logger') {
        return classMatch[1];
      }
    }
    
    // Fallback to component name if we can't find a class
    const componentMatch = stackLines[3]?.match(/at\s+([A-Z][a-zA-Z0-9]*)/);
    return componentMatch?.[1] || 'Unknown';
  }

  // Core logging methods
  log(...args) {
    if (this.isStaging) {
      const caller = this.getCallerName();
      console.log(`[${caller}]`, ...args);
    }
  }

  info(...args) {
    if (this.isStaging) {
      const caller = this.getCallerName();
      console.info(`[${caller}]`, ...args);
    }
  }

  warn(...args) {
    if (this.isStaging) {
      const caller = this.getCallerName();
      console.warn(`[${caller}]`, ...args);
    }
  }

  error(...args) {
    // Always log errors, even in production
    const caller = this.getCallerName();
    console.error(`[${caller}]`, ...args);
  }

  debug(...args) {
    if (this.isStaging) {
      const caller = this.getCallerName();
      console.debug(`[${caller}]`, ...args);
    }
  }

  // Group logging methods
  group(label) {
    if (this.isStaging) {
      const caller = this.getCallerName();
      console.group(`[${caller}] ${label}`);
    }
  }

  groupCollapsed(label) {
    if (this.isStaging) {
      const caller = this.getCallerName();
      console.groupCollapsed(`[${caller}] ${label}`);
    }
  }

  groupEnd() {
    if (this.isStaging) {
      console.groupEnd();
    }
  }

  // Utility methods
  table(data) {
    if (this.isStaging) {
      console.table(data);
    }
  }

  trace() {
    if (this.isStaging) {
      console.trace();
    }
  }

  // Environment-specific logging
  environment() {
    if (this.isStaging) {
      this.log(`Running in ${process.env.REACT_APP_ENVIRONMENT || 'staging'} mode`);
      this.log(`REACT_APP_ENVIRONMENT: ${process.env.REACT_APP_ENVIRONMENT || 'not set (defaulting to staging)'}`);
    }
  }

  // Method to log API calls (only in staging)
  apiCall(method, url, status, duration = null) {
    if (this.isStaging) {
      const message = duration 
        ? `${method} ${url} - ${status} (${duration}ms)`
        : `${method} ${url} - ${status}`;
      this.info(`API: ${message}`);
    }
  }

  // Method to log component lifecycle (only in staging)
  component(componentName, action, data = null) {
    if (this.isStaging) {
      const message = data 
        ? `Component: ${componentName} - ${action} - ${JSON.stringify(data)}`
        : `Component: ${componentName} - ${action}`;
      this.debug(message);
    }
  }

  // Method to log user actions (only in staging)
  userAction(action, details = null) {
    if (this.isStaging) {
      const message = details 
        ? `User Action: ${action} - ${JSON.stringify(details)}`
        : `User Action: ${action}`;
      this.info(message);
    }
  }
}

// Create and export singleton instance
export const logger = new Logger();

// Export the class for testing or custom instances
export { Logger };

// Export convenience methods for direct usage
export const log = (...args) => logger.log(...args);
export const info = (...args) => logger.info(...args);
export const warn = (...args) => logger.warn(...args);
export const error = (...args) => logger.error(...args);
export const debug = (...args) => logger.debug(...args);
